// Create a new file for cross-tab communication

export class AuthChannel {
  private channel: BroadcastChannel | null = null;
  private channelName: string;

  constructor() {
    // Use domain-specific channel name in production
    this.channelName = window.location.hostname.includes('wanttd.com') 
      ? 'auth_channel_prod'
      : 'auth_channel_dev';
      
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(this.channelName);
      } catch (error) {
        console.error('Failed to create BroadcastChannel:', error);
      }
    }
  }

  // Listen for auth events from other tabs
  public listen(onLogout: () => void, onLogin: () => void): void {
    if (!this.channel) {
      // Fallback to localStorage for cross-tab communication
      window.addEventListener('storage', (event) => {
        if (event.key === 'auth_event') {
          const data = JSON.parse(event.newValue || '{}');
          if (data.type === 'LOGOUT') onLogout();
          if (data.type === 'LOGIN') onLogin();
        }
      });
      return;
    }
    
    this.channel.onmessage = (event) => {
      if (event.data.type === 'LOGOUT') {
        onLogout();
      } else if (event.data.type === 'LOGIN') {
        onLogin();
      }
    };
  }

  // Broadcast logout to other tabs
  public broadcastLogout(): void {
    if (this.channel) {
      try {
        this.channel.postMessage({ type: 'LOGOUT' });
      } catch (error) {
        console.error('Error broadcasting via channel:', error);
      }
    }
    
    // Also use localStorage as fallback
    try {
      console.log('Broadcasting logout to other tabs');
      localStorage.setItem('auth_logout_event', Date.now().toString());
      
      // Also dispatch a custom event for the current tab
      window.dispatchEvent(new CustomEvent('auth_logout'));
      
      // Force removal of authentication items
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userId');
      sessionStorage.removeItem('isAuthenticated');
      sessionStorage.removeItem('_temp_token');
      
      console.log('Logout broadcast complete');
    } catch (error) {
      console.error('Error broadcasting logout:', error);
    }
  }
  
  // Broadcast login to other tabs
  public broadcastLogin(): void {
    if (this.channel) {
      try {
        this.channel.postMessage({ type: 'LOGIN' });
      } catch (error) {
        console.error('Error broadcasting via channel:', error);
      }
    }
    
    try {
      console.log('Broadcasting login to other tabs');
      
      // Use multiple storage mechanisms to increase chances of cross-tab detection
      localStorage.setItem('auth_login_event', Date.now().toString());
      localStorage.setItem('auth_status', 'logged_in');
      localStorage.setItem('auth_login_timestamp', Date.now().toString());
      
      // Use sessionStorage for the current tab
      sessionStorage.setItem('isAuthenticated', 'true');
      
      // Dispatch multiple events for redundancy
      window.dispatchEvent(new CustomEvent('auth_login'));
      window.dispatchEvent(new Event('storage')); // Simulate storage event
      window.dispatchEvent(new Event('tokenChange'));
      
      console.log('Login broadcast complete');
    } catch (error) {
      console.error('Error broadcasting login:', error);
    }
  }

  // Clean up
  public close(): void {
    if (this.channel) {
      try {
        this.channel.close();
      } catch (error) {
        console.error('Error closing auth channel:', error);
      } finally {
        this.channel = null;
      }
    }
  }

  // Add a method to listen for logout events
  public listenForLogout(callback: () => void): () => void {
    // Listen for storage events (other tabs)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'auth_logout_event') {
        console.log('Received logout event from another tab');
        callback();
      }
    };
    
    // Listen for custom events (current tab)
    const handleLogoutEvent = () => {
      console.log('Received logout event in current tab');
      callback();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth_logout', handleLogoutEvent);
    
    // Return a function to remove the listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth_logout', handleLogoutEvent);
    };
  }

  // Add a listener for login events
  public listenForLogin(callback: () => void): () => void {
    // Listen for storage events (other tabs)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'auth_login_event') {
        console.log('Received login event from another tab');
        callback();
      }
    };
    
    // Listen for custom events (current tab)
    const handleLoginEvent = () => {
      console.log('Received login event in current tab');
      callback();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth_login', handleLoginEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth_login', handleLoginEvent);
    };
  }
}

export const authChannel = new AuthChannel(); 